import { ApiResponse } from "@/types/api.types.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { useState } from "react";

interface DownloadOptions {
  apiCall: () => Promise<ApiResponse<BlobPart>>;
  onSuccess?: () => void;
  onFinally?: () => void;
}

// interface DownloadHelper {
//   isExporting: boolean;
//   downloadFile: (options: DownloadOptions) => void;
// }

export const useDownloadHelper = () => {
  const { handleErrors } = useErrorHandle();
  const [isExporting, setIsExporting] = useState(false);

  const extractFileName = (contentDisposition: string | undefined): string => {
    if (!contentDisposition) return "download";
    const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
    return fileNameMatch?.length === 2 ? fileNameMatch[1] : "download";
  };

  const createDownloadLink = (data: BlobPart, fileName: string): void => {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadFile = ({ apiCall, onSuccess, onFinally }: DownloadOptions): void => {
    setIsExporting(true);

    apiCall()
      .then((response) => {
        if (response.success && response.data && response.headers) {
          const fileName = extractFileName(response.headers["content-disposition"]);
          createDownloadLink(response.data, fileName);
          onSuccess?.();
        } else if (response.error) {
          handleErrors(response.error);
        }
      })
      .finally(() => {
        setIsExporting(false);
        onFinally?.();
      });
  };

  const downloadFileFromUrl = (url: string) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const downloadFileFromUrlWithName = async (url: string, fileName: string): Promise<void> => {
    const blob = await fetch(url).then((r) => r.blob());
    createDownloadLink(blob, fileName);
  };

  return {
    isExporting,
    downloadFile,
    downloadFileFromUrl,
    downloadFileFromUrlWithName,
    createDownloadLink,
  };
};
